import { useEffect, useMemo, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "shared/components/Popover";
import { Template } from "types/landlord/verificationTemplate";
import { getErrorMessage } from "utils/error";
import templatesApi, { GetTemplatesResponse } from "api/landlord/templates";
import { ChangeTemplateWarningModal } from "./ChangeTemplateWarningModal";
import { ApplicationStatus } from "shared/types";
import { TemplateMenuContent, TemplateMenuItem } from "../TemplateMenuContent";

const PER_PAGE = 20;

const isNewerVersion = (template: Template, currentTemplate?: Template) =>
  currentTemplate !== undefined &&
  template.id === currentTemplate.id &&
  (template.version ?? 0) > (currentTemplate.version ?? 0);

const isCurrentTemplate = (template: Template, currentTemplate?: Template) =>
  currentTemplate !== undefined &&
  template.id === currentTemplate.id &&
  (template.version ?? 0) === (currentTemplate.version ?? 0);

const toMenuItem = (
  template: Template,
  currentTemplate?: Template
): TemplateMenuItem => ({
  id: template.id,
  name: template.name,
  isDefault: template.is_property_default ?? Boolean(template.default),
  isCurrent: isCurrentTemplate(template, currentTemplate),
  isNewVersion: isNewerVersion(template, currentTemplate),
  disabled: isCurrentTemplate(template, currentTemplate)
});

export const ChangeTemplateSubmenu = ({
  applicationId,
  applicationStatus,
  currentTemplate,
  onTemplateChanged,
  contentClassName,
  propertyId
}: {
  applicationId: string;
  applicationStatus?: ApplicationStatus;
  currentTemplate?: Template;
  onTemplateChanged?: () => void;
  contentClassName?: string;
  propertyId?: number;
}) => {
  const [open, setOpen] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );
  const [showAllTemplates, setShowAllTemplates] = useState(false);

  const queryClient = useQueryClient();

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInfiniteQuery<GetTemplatesResponse>({
      queryKey: ["TEMPLATES", { property_id: propertyId }],
      queryFn: ({ pageParam }) =>
        templatesApi.getTemplates({
          page: Number(pageParam),
          per_page: PER_PAGE,
          property_id: propertyId
        }),
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        lastPage.has_more ? lastPage.current_page + 1 : undefined
    });

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, data?.pages.length]);

  const { mutate: changeTemplate, isPending } = useMutation({
    mutationFn: () => {
      if (!selectedTemplateId) throw new Error("No template selected");
      return templatesApi.changeApplicationTemplate(
        applicationId,
        selectedTemplateId
      );
    },
    mutationKey: ["APPLICATION_TEMPLATE", applicationId, "UPDATE"],
    onSuccess: (data) => {
      if (data.warnings && data.warnings.length > 0)
        data.warnings.forEach((warning) => {
          toast.warning(warning.message);
        });
      else toast.success("Template changed successfully");

      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", applicationId]
      });
      queryClient.invalidateQueries({ queryKey: ["APPLICANTS_TABLE"] });

      onTemplateChanged?.();
      setShowWarningModal(false);
      setSelectedTemplateId(null);

      setTimeout(() => window.location.reload(), 2000);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    }
  });

  const allTemplates = data?.pages.flatMap((page) => page.items) ?? [];

  const sortedTemplates = useMemo(
    () =>
      allTemplates.slice().sort((a, b) => {
        const aCurrent = isCurrentTemplate(a, currentTemplate);
        const bCurrent = isCurrentTemplate(b, currentTemplate);
        const aNew = isNewerVersion(a, currentTemplate);
        const bNew = isNewerVersion(b, currentTemplate);
        if (aCurrent !== bCurrent) return aCurrent ? -1 : 1;
        if (aNew !== bNew) return aNew ? -1 : 1;
        return a.name.localeCompare(b.name);
      }),
    [allTemplates, currentTemplate]
  );

  const { propertyItems, otherItems } = useMemo(() => {
    if (!propertyId)
      return {
        propertyItems: sortedTemplates.map((t) =>
          toMenuItem(t, currentTemplate)
        ),
        otherItems: [] as TemplateMenuItem[]
      };

    return sortedTemplates.reduce<{
      propertyItems: TemplateMenuItem[];
      otherItems: TemplateMenuItem[];
    }>(
      (acc, t) => {
        const item = toMenuItem(t, currentTemplate);
        if (t.is_property_template || isNewerVersion(t, currentTemplate))
          acc.propertyItems.push(item);
        else acc.otherItems.push(item);

        return acc;
      },
      { propertyItems: [], otherItems: [] }
    );
  }, [sortedTemplates, propertyId, currentTemplate]);

  const isStatusInvalid = !Boolean(
    applicationId &&
    applicationStatus &&
    [ApplicationStatus.Submitted].includes(applicationStatus)
  );

  const hasNoSelectableTemplates =
    !isLoading &&
    !isFetchingNextPage &&
    sortedTemplates.filter((t) => !isCurrentTemplate(t, currentTemplate))
      .length === 0;

  let tooltipMessage: string | null = null;
  if (hasNoSelectableTemplates)
    tooltipMessage = "There are no other templates available to change to";
  else if (isStatusInvalid)
    tooltipMessage =
      "Template change is not available for partially submitted, not submitted, decisioned, canceled, and on-hold applications";

  const disabled = Boolean(tooltipMessage);

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setShowWarningModal(true);
    setOpen(false);
  };

  if (isLoading)
    return (
      <Button
        variant={ButtonVariant.BASE}
        size={ButtonSize.SM}
        className="w-full"
        disabled
        loading
      >
        Change template
      </Button>
    );

  return (
    <>
      <Tooltip open={disabled ? undefined : false} placement="bottom-start">
        <TooltipTrigger asChild>
          <div>
            <Popover open={open} onOpenChange={setOpen} placement="right-start">
              <PopoverTrigger
                disabled={disabled}
                onClick={(e) => {
                  if (disabled) return;
                  e.stopPropagation();
                  e.preventDefault();
                  setOpen((v) => !v);
                }}
              >
                <Button
                  variant={ButtonVariant.BASE}
                  size={ButtonSize.SM}
                  className="w-full"
                  disabled={disabled}
                >
                  Change template
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className={`!z-[105] w-[309px] max-h-[300px] overflow-y-auto shadow-xl rounded-lg border bg-background-1000 ${contentClassName ?? ""}`}
              >
                <TemplateMenuContent
                  sectionTitle={
                    propertyId &&
                    propertyItems.length > 0 &&
                    otherItems.length > 0
                      ? "Templates for this property"
                      : undefined
                  }
                  items={propertyItems}
                  otherItems={otherItems}
                  showAll={showAllTemplates}
                  onShowAll={() => setShowAllTemplates(true)}
                  onSelect={handleTemplateSelect}
                />
              </PopoverContent>
            </Popover>
          </div>
        </TooltipTrigger>
        {tooltipMessage ? (
          <TooltipContent className="max-w-[400px] rounded-lg bg-alpha-800 p-4 text-sm text-white z-[106] border">
            {tooltipMessage}
          </TooltipContent>
        ) : null}
      </Tooltip>

      <ChangeTemplateWarningModal
        opened={showWarningModal}
        onClose={() => {
          setShowWarningModal(false);
          setSelectedTemplateId(null);
        }}
        onContinue={() => changeTemplate()}
        isPending={isPending}
      />
    </>
  );
};
